import type { Json, RecordData } from "../core";
import { enumKey } from "../managers/cell_utils";

/**
 * Per-row invisible-expression evaluator. Used by ListView for row buttons
 * and (future) field-level per-row gates. Returns TRUE when the expression
 * evaluates to "hide this row-scope element for THIS record".
 *
 * Supported expression shapes (covers the common DSL patterns):
 *   - ""                          → visible (no expr = always show)
 *   - "field"                     → hide when record.field is truthy
 *   - "not field"                 → hide when record.field is falsy
 *   - "field == 'value'"          → equality (string / number / bool / null literals)
 *   - "field != 'value'"          → inequality
 *   - "field in ['a','b']"        → membership (hide when value is in the list)
 *   - "field not in ['a','b']"    → non-membership (hide when value is NOT in the list)
 *   - "<a> and <b>"               → both must be truthy to hide
 *   - "<a> or <b>"                → either truthy hides
 *
 * Anything unrecognised → returns FALSE (visible) so a malformed expression
 * never silently hides UI — fail-open at the row level (the opposite of the
 * view-render strip pass, which fails closed at the view level).
 *
 * NB: this is a pragmatic per-row evaluator. Full predicate expressions with
 * `config(...)`, `has_role(...)` etc. are view-context invariants and are
 * already stripped server-side at view-render time — they don't run per row.
 */

const IDENT_RE = /[a-zA-Z_][a-zA-Z0-9_]*/;
const COMPARE_OPS = ["==", "!=", "<=", ">=", "<", ">"] as const;

type CompareOp = typeof COMPARE_OPS[number];

const isTruthy = (v: unknown): boolean => v != null && v !== "" && v !== false && v !== 0;

function parseLiteral(s: string): Json | null {
    const t = s.trim();
    if ((t.startsWith("'") && t.endsWith("'")) || (t.startsWith('"') && t.endsWith('"'))) {
        return t.slice(1, -1);
    }
    if (t === "true") return true;
    if (t === "false") return false;
    if (t === "null" || t === "none" || t === "None") return null;
    const n = Number(t);
    if (!Number.isNaN(n) && /^-?\d+(\.\d+)?$/.test(t)) return n;
    return t;
}

// Parse a `[ 'a', 'b', 1 ]` list literal into an array of parsed values.
// Returns null when `s` is not a bracketed list.
function parseListLiteral(s: string): (Json | null)[] | null {
    const t = s.trim();
    if (!t.startsWith("[") || !t.endsWith("]")) return null;
    const inner = t.slice(1, -1).trim();
    if (!inner) return [];
    return inner.split(",").map((part) => parseLiteral(part));
}

const MEMBERSHIP_RE = /^(.+?)\s+(not\s+in|in)\s+(\[.*\])$/;

function compare(lhs: unknown, op: CompareOp, rhs: unknown): boolean {
    // Enum values arrive as the rich read shape [key, label]; compare on the
    // stored key so predicates like `status == 'DRAFT'` still match.
    // (enumKey leaves scalars and m2o {id,display_name} objects untouched.)
    const lhsKey = enumKey(lhs);
    // null / undefined normalised so "== null" works for both.
    const l = lhsKey == null ? null : lhsKey;
    const r = rhs == null ? null : rhs;
    switch (op) {
        case "==": return l === r;
        case "!=": return l !== r;
        case "<":  return (l as number) < (r as number);
        case "<=": return (l as number) <= (r as number);
        case ">":  return (l as number) > (r as number);
        case ">=": return (l as number) >= (r as number);
    }
}

function evalAtom(expr: string, record: RecordData): boolean {
    const e = expr.trim();
    if (!e) return false;

    // Bare literal truthy / falsy — `invisible="1"` and `invisible="true"`
    // (always-invisible) are the most common forms in the DSL. Without
    // this branch they fall through to the bare-identifier check, miss
    // the IDENT_RE (digits / boolean keywords don't qualify), and the
    // evaluator returns false (= visible). That breaks `invisible="1"`
    // for the form-view back-reference hiding pattern.
    if (e === "1" || e === "true" || e === "True") return true;
    if (e === "0" || e === "false" || e === "False") return false;

    // <field> in [list] / <field> not in [list] — checked before the bare
    // `not ` prefix and the compare loop so the infix `not in` is matched
    // whole rather than mis-parsed.
    const memberMatch = e.match(MEMBERSHIP_RE);
    if (memberMatch) {
        const left = memberMatch[1].trim();
        const negate = memberMatch[2].replace(/\s+/g, " ") === "not in";
        const list = parseListLiteral(memberMatch[3]);
        if (list != null && IDENT_RE.test(left) && left.match(IDENT_RE)?.[0] === left) {
            const present = list.some((item) => compare(record[left], "==", item));
            return negate ? !present : present;
        }
    }

    // not <atom>
    if (e.startsWith("not ")) {
        return !evalAtom(e.slice(4), record);
    }

    // <field> <op> <literal>
    for (const op of COMPARE_OPS) {
        const idx = e.indexOf(op);
        if (idx > 0) {
            const left = e.slice(0, idx).trim();
            const right = e.slice(idx + op.length).trim();
            if (IDENT_RE.test(left) && left.match(IDENT_RE)?.[0] === left) {
                return compare(record[left], op, parseLiteral(right));
            }
        }
    }

    // bare identifier — truthy check on record[field]
    if (e.match(IDENT_RE)?.[0] === e) {
        return isTruthy(record[e]);
    }

    // Unrecognised atom → false (visible).
    return false;
}

export function evalRowInvisible(expression: string | null | undefined, record: RecordData): boolean {
    if (!expression) return false;
    const expr = expression.trim();
    if (!expr) return false;

    // Split on top-level " or " first (lowest precedence), then " and ".
    // Top-level only — no paren balancing yet (good enough for the DSL forms
    // we actually see; extend if a real consumer needs nesting).
    const orParts = expr.split(/\s+or\s+/);
    if (orParts.length > 1) {
        return orParts.some((p) => evalRowInvisible(p, record));
    }
    const andParts = expr.split(/\s+and\s+/);
    if (andParts.length > 1) {
        return andParts.every((p) => evalRowInvisible(p, record));
    }
    return evalAtom(expr, record);
}
